import { useRef, useState } from 'react';

import {
  ArrowCircleDown as BuyIcon,
  DoDisturbAlt as CancelIcon,
  SwapVerticalCircle as CurrentPriceIcon,
  Notifications,
  NotificationsNone,
  ArrowCircleRight as OpenTradeIcon,
  Timeline as ReferencePriceIcon,
  ArrowCircleUp as SellIcon,
  Settings,
  VolumeMute as SoundsOffIcon,
  VolumeUp as SoundsOnIcon,
  OilBarrel as SymbolIcon,
  GpsNotFixed as TargetPriceIcon,
  CalendarToday as TenorIcon,
} from '@mui/icons-material';
import darkTheme from '@shared/themes/darkTheme';

import { ButtonStyle, CellRendererStyle, DisabledButtonStyle, ListIconButtonStyle, ListIconButtonStyleNoHover, TextStyles } from './WatchlistStyles';

import { Box, Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Popover, Theme } from '@mui/material';
import { NotificationPlayer, NotificationSounds } from '@shared/assets/sounds/NotificationPlayer';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { ESide, WatchlistEntry, WatchlistEntryOptions } from './WatchlistTypes';

/*
Headers
*/

const HeaderCell = (props: React.PropsWithChildren & { title: string }) => {
  return <div title={props.title}>{props.children}</div>;
};

export const Headers = {
  Symbol: () => {
    return (
      <HeaderCell title="Symbol">
        <SymbolIcon />
      </HeaderCell>
    );
  },

  CurrentPriceHeader: () => {
    return (
      <HeaderCell title="Current Price">
        <CurrentPriceIcon />
      </HeaderCell>
    );
  },

  TargetPrice: () => {
    return (
      <HeaderCell title="Target Price">
        <TargetPriceIcon />
      </HeaderCell>
    );
  },

  Tenor: () => {
    return (
      <HeaderCell title="Tenor">
        <TenorIcon />
      </HeaderCell>
    );
  },

  Buttons: () => {
    return (
      <HeaderCell title="Actions & Options">
        <OpenTradeIcon />
      </HeaderCell>
    );
  },

  ReferencePriceHeader: () => {
    return (
      <HeaderCell title="Reference Price">
        <ReferencePriceIcon />
      </HeaderCell>
    );
  },
};

/*
Cell renderer for target price
*/

export const TargetPriceCellComponent = (data: CustomCellRendererProps<WatchlistEntry>) => {
  if (!data.data) return <></>;

  let colour = data.data.side === ESide.BUY ? darkTheme.palette.background.buy : darkTheme.palette.background.sell;

  if (data.data.targetPriceHit) {
    colour = '#FFFFFF';
  }

  const icon =
    data.data?.side == ESide.BUY ? (
      <BuyIcon sx={{ color: colour, flexGrow: 1, position: 'relative', top: '-2px' }} />
    ) : (
      <SellIcon sx={{ color: colour, flexGrow: 1, position: 'relative', top: '-2px' }} />
    );

  const iconOpacity = data.data?.side === ESide.UNSET ? 0 : 1;

  return (
    <div style={{ display: 'flex', whiteSpace: 'nowrap', cursor: 'edit', paddingTop: '1px', justifyContent: 'space-around' }}>
      <div style={{ flexGrow: 1, position: 'relative', paddingLeft: '.5em' }}>{data.data?.targetPrice.toFixed(2)}</div>
      <div style={{ flexGrow: 1, minWidth: 0, paddingRight: '.5em', opacity: iconOpacity }}>{icon}</div>
    </div>
  );
};

/* 
Cell renderer for current price
*/

export const CurrentPriceCellComponent = (data: CustomCellRendererProps<WatchlistEntry>) => {
  if (!data.data) {
    return <div>-</div>;
  }

  if (!data.data.hasUpdated) {
    return <div>-</div>;
  }

  const { spread, options, hasSpreadData, targetPriceHit } = data.data;
  const { bid, ask, mid } = spread;
  const { showBid, showAsk } = options;
  const bidStyle: React.CSSProperties = { ...TextStyles.bid, opacity: showBid ? 1 : 0, flexShrink: 1, position: 'absolute', left: '.5em' };
  const askStyle: React.CSSProperties = { ...TextStyles.ask, opacity: showAsk ? 1 : 0, flexShrink: 1, position: 'absolute', right: '.5em' };

  if (targetPriceHit) {
    bidStyle.color = darkTheme.palette.background.buyHover;
    askStyle.color = darkTheme.palette.background.sellHover;
  }

  const midStyle: React.CSSProperties = { textAlign: 'center', flexGrow: 1 };

  const bidContent = hasSpreadData ? bid.toFixed(2) : '-';
  const askContent = hasSpreadData ? ask.toFixed(2) : '-';

  return (
    <div style={CellRendererStyle}>
      <span style={bidStyle}>{bidContent}</span>
      <span style={midStyle}>{mid.toFixed(2)}</span>
      <span style={askStyle}>{askContent}</span>
    </div>
  );
};

export const ReferencePriceComponent = (data: CustomCellRendererProps<WatchlistEntry>) => {
  if (!data.data) {
    return <div>-</div>;
  }

  const { referencePrice, spread } = data.data;
  const delta = spread.mid - referencePrice;

  return (
    <div style={{ ...CellRendererStyle, gap: 4, justifyContent: 'space-between', padding: '0 .5em' }}>
      <div style={{ textAlign: 'center', padding: 0 }}>{referencePrice.toFixed(2)}</div>
      <div style={{ textAlign: 'center', padding: 0, fontSize: '1em' }}>({delta.toFixed(2)})</div>
    </div>
  );
};

type WatchlistOptionsPopupProps = {
  onUpdateOption: (options: Partial<WatchlistEntryOptions>) => void;
  anchorEl: React.RefObject<HTMLDivElement | null>;
  open: boolean;
  options: WatchlistEntryOptions;
  setIsOpen: (isOpen: boolean) => void;
  title?: string;
  hideOnMouseOut: boolean;
};

enum OptionsSection {
  None = 0,
  Notifications = 1,
  BidAsk = 2,
  Sounds = 3,
}

export const WatchlistRowOptionsPopup = (props: WatchlistOptionsPopupProps) => {
  const { open, setIsOpen, anchorEl } = props;

  const { notifications, sounds, showBid, showAsk } = props.options;
  const title = props.title || 'Options';

  const notificationStateText = notifications ? 'Notifications On' : 'Notifications Off';
  const soundsStateText = sounds > 0 ? NotificationSounds[sounds].name : 'No Alert Sound';

  const [focussedSection, setFocussedSection] = useState<OptionsSection>(OptionsSection.None);

  const toggleNotifications = (onOrOff: boolean) => {
    props.onUpdateOption({ notifications: onOrOff });
  };

  const toggleBid = () => {
    props.onUpdateOption({ showBid: !showBid });
  };

  const toggleAsk = () => {
    props.onUpdateOption({ showAsk: !showAsk });
  };

  const setSound = (soundId: number, play: boolean = false) => {
    props.onUpdateOption({ sounds: soundId });
  };

  const previewSound = (soundId: number) => {
    NotificationPlayer.get().playSoundByIndex(soundId, true);
  };

  const updateFocus = (section: OptionsSection) => {
    setFocussedSection(section);
  };

  const BidStyle = (theme: Theme) => {
    return {
      backgroundColor: theme.palette.background.buy,
      transition: 'all 0.1s ease',
      filter: showBid ? 'none' : 'saturate(20%)',
      color: showBid ? 'white' : '#DDD',
      ':hover': {
        backgroundColor: theme.palette.background.buyHover,
      },
      justifyContent: 'center',
    };
  };

  const AskStyle = (theme: Theme) => {
    return {
      backgroundColor: theme.palette.background.sell,
      transition: 'all 0.1s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      filter: showAsk ? 'none' : 'saturate(20%)',
      color: showAsk ? 'white' : '#DDD',
      ':hover': {
        backgroundColor: theme.palette.background.sellHover,
      },
    };
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl.current}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ pointerEvents: 'none', transition: 'all 0.2s ease' }}
    >
      <Box
        sx={{ pointerEvents: 'all', borderRadius: 0 }}
        onMouseLeave={() => {
          if (!props.hideOnMouseOut) return;
          updateFocus(OptionsSection.None);
          setIsOpen(false);
        }}
      >
        <List sx={{ minWidth: '16em' }} disablePadding component="div" subheader={<li />}>
          <ListSubheader
            sx={theme => {
              return { pointerEvents: 'none', backgroundColor: theme.palette.background.popover };
            }}
          >
            {title}
          </ListSubheader>

          {/* Bid and ask */}
          <ListItem
            onMouseEnter={() => {
              setFocussedSection(OptionsSection.BidAsk);
            }}
            style={{ padding: '0' }}
            disablePadding={true}
            disableGutters={true}
          >
            <ListItemButton sx={BidStyle} onClick={toggleBid}>
              <span>Show Bid</span>
            </ListItemButton>
            <div style={{ width: '4px' }}></div>
            <ListItemButton sx={AskStyle} onClick={toggleAsk}>
              <span>Show Ask</span>
            </ListItemButton>
          </ListItem>

          {/* Toggle notifications */}
          <ListItemButton
            onClick={() => {
              toggleNotifications(!notifications);
            }}
            onMouseEnter={() => {
              updateFocus(OptionsSection.Notifications);
            }}
          >
            <ListItemText primary={notificationStateText} />
            <ListItemIcon sx={{ minWidth: 0 }}>{notifications ? <Notifications /> : <NotificationsNone />}</ListItemIcon>
          </ListItemButton>

          {/* Sounds */}
          <ListItemButton
            onMouseEnter={() => {
              setFocussedSection(OptionsSection.Sounds);
            }}
          >
            <ListItemText primary={soundsStateText} />
            <ListItemIcon sx={{ minWidth: 0 }}>{sounds > 0 ? <SoundsOnIcon onClick={() => previewSound(sounds)} /> : <SoundsOffIcon />}</ListItemIcon>
          </ListItemButton>

          <Collapse in={focussedSection === OptionsSection.Sounds} appear={true} unmountOnExit mountOnEnter>
            <List disablePadding sx={{ transition: 'all 0.2s ease !important' }}>
              {NotificationSounds.map((s, i) => {
                return (
                  <ListItemButton
                    key={s.name}
                    selected={sounds === i}
                    onClick={() => {
                      setSound(i, true);
                    }}
                    sx={{ pl: 4, span: { opacity: '0.6' }, '&.Mui-selected span': { opacity: 1 }, ':hover span': { opacity: 1.0 } }}
                  >
                    <ListItemText primary={s.name} />

                    {i === 0 ? (
                      <SoundsOffIcon sx={ListIconButtonStyleNoHover} />
                    ) : (
                      <ListItemIcon
                        sx={ListIconButtonStyle}
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault();
                          previewSound(i);
                        }}
                      >
                        <SoundsOnIcon />
                      </ListItemIcon>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Collapse>
        </List>
      </Box>
    </Popover>
  );
};

export type WatchRowButtonComponentParams = {
  onCancel: (data?: WatchlistEntry) => void;
  onOpenTrade: (data?: WatchlistEntry) => void;
  onOptionsUpdate: (data: WatchlistEntry, options: Partial<WatchlistEntryOptions>) => void;
};

export const RowButtonCellComponent = (props: CustomCellRendererProps<WatchlistEntry> & WatchRowButtonComponentParams) => {
  const [open, setIsOpen] = useState(false);
  const anchorEl = useRef<HTMLDivElement | null>(null);

  if (props.data === undefined) return <div></div>;

  const SettingsIcon = Settings;

  const tradeButtonEnabled = props.data.canTrade || false;

  const openTrade = () => {
    if (tradeButtonEnabled) props.onOpenTrade(props.data);
  };

  return (
    <div style={{ display: 'flex', justifyItems: 'stretch', position: 'relative', top: '-1px' }}>
      {/* Cancel */}
      <CancelIcon sx={ButtonStyle} onClick={() => props.onCancel(props.data)} data-testid={'cancel-' + props.data.id} />

      {/* Options */}
      <>
        <div style={{ display: 'inline-block', padding: 0, margin: 0 }} ref={anchorEl}>
          <SettingsIcon
            sx={ButtonStyle}
            onClick={(e: React.MouseEvent<Element>) => {
              (e.currentTarget as HTMLElement).blur();
              setIsOpen(!open);
            }}
            data-testid={'settings-' + props.data.id}
          />
        </div>

        <WatchlistRowOptionsPopup
          open={open}
          setIsOpen={setIsOpen}
          anchorEl={anchorEl}
          options={props.data!.options}
          onUpdateOption={(update: Partial<WatchlistEntryOptions>) => {
            props.onOptionsUpdate(props.data!, update);
          }}
          hideOnMouseOut={true}
          title={props.data.symbol.toUpperCase()}
        />
      </>
      {/* Open Trade */}
      <OpenTradeIcon sx={tradeButtonEnabled ? ButtonStyle : DisabledButtonStyle} onClick={openTrade} data-testid={'trade-' + props.data.id} />
    </div>
  );
};
